import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  cancelSubscription,
  getBilling,
  getCompanyIndustry,
  getCustomerSetupChecklist,
  getRelatedCustomers,
  getTeamMembers,
  getUser,
  postAddCustomer,
  postAddTeamMember,
  postChangePassword,
  postConfirmEmailVerificationExisting,
  postConfirmVerificationToken,
  postConfirmVerificationTokenExistingEmail,
  postDeleteTeamMember,
  postForgotPassword,
  postJitaLogout,
  postJwtForAccount,
  postLogin,
  postLogout,
  postResendEmailVerificationExisting,
  postResendInviteTeamMember,
  postResendVerificationEmail,
  postResetPassword,
  postSignup,
  postUpdateTeamMember,
  reprocessSubscription,
  restartSubscription,
} from "@/requests/api/user";

export const useUser = (
  props?: Omit<UseQueryOptions<IGetUserRS>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: ["user"],
    queryFn: getUser,
    retryOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: 1000 * 60 * 5,
    enabled: true,
    ...props,
  });

export const useLogin = () =>
  useMutation({
    mutationFn: postLogin,
  });

export const usePostLogout = () =>
  useMutation({
    mutationFn: postLogout,
  });

export const usePostResetPassword = () =>
  useMutation({
    mutationFn: postResetPassword,
  });

export const useResendVerificationEmail = () =>
  useMutation({
    mutationFn: postResendVerificationEmail,
  });

export const useGetJWTForAccount = () =>
  useMutation({
    mutationFn: postJwtForAccount,
  });

export const useRelatedCustomers = (params?: IUseRelatedCustomersRS) => {
  const enabled = typeof params?.enabled === "boolean" ? params?.enabled : true;

  return useQuery({
    queryKey: ["customers"],
    queryFn: getRelatedCustomers,
    staleTime: 1000 * 20,
    enabled,
  });
};

export const useForgotPassword = () =>
  useMutation({
    mutationFn: postForgotPassword,
  });

export const useConfirmVerificationToken = () =>
  useMutation({
    mutationFn: postConfirmVerificationToken,
  });

export const useVerifyExisting = () =>
  useMutation({
    mutationFn: postConfirmEmailVerificationExisting,
  });

export const useResendVerifyExisting = () =>
  useMutation({
    mutationFn: postResendEmailVerificationExisting,
  });

export const useVerifyExistingCode = () =>
  useMutation({
    mutationFn: postConfirmVerificationTokenExistingEmail,
  });

export const usePostSignup = () =>
  useMutation({
    mutationFn: postSignup,
  });

export const useChangePassword = () =>
  useMutation({
    mutationFn: postChangePassword,
  });

export const useGetBilling = () =>
  useQuery({
    queryKey: ["billing"],
    queryFn: getBilling,
  });

export const useCancelSubscription = () =>
  useMutation({
    mutationFn: cancelSubscription,
  });

export const useRestartSubscription = () =>
  useMutation({
    mutationFn: restartSubscription,
  });

export const useReprocessSubscription = () =>
  useMutation({
    mutationFn: reprocessSubscription,
  });

export const usePostJitaLogout = () =>
  useMutation({
    mutationFn: postJitaLogout,
  });

export const usePostAddCustomer = () =>
  useMutation({
    mutationFn: postAddCustomer,
  });

export const useGetCompanyIndustry = () =>
  useQuery({
    queryKey: ["company-industry"],
    queryFn: getCompanyIndustry,
  });

export const useGetTeamMembers = ({
  enabled = true,
  params,
}: {
  enabled?: boolean;
  params: IGetTeamMemberParams;
}) =>
  useQuery({
    queryKey: ["settings", "team-members"],
    queryFn: ({ signal }) => getTeamMembers({ signal, params }),
    enabled,
  });

export const usePostAddTeamMember = () =>
  useMutation({
    mutationFn: postAddTeamMember,
  });

export const usePostUpdateTeamMember = () =>
  useMutation({
    mutationFn: postUpdateTeamMember,
  });

export const usePostDeleteTeamMember = () =>
  useMutation({
    mutationFn: postDeleteTeamMember,
  });

export const usePostResendInviteTeamMember = () =>
  useMutation({
    mutationFn: postResendInviteTeamMember,
  });

export const useGetCustomerSetupChecklist = (enabled: boolean) =>
  useQuery({
    enabled,
    queryKey: ["customer-setup-checklist"],
    queryFn: getCustomerSetupChecklist,
  });
